import { AssignMobileOfferRequest, MobileOfferCatalogEntry, MobileOfferCustomer } from '@numeo/types'
import { CustomerActionDialog } from './CustomerActionDialog'
import { CustomerOfferSummary } from './CustomerOfferSummary'
import { customerOfferDate, customerStoreName } from './customer-offer-utils'
import { assignmentStores } from './assignmentStores'
import { useBothStoreAssignment } from './useBothStoreAssignment'
import { OfferButton, OfferNotice } from './ui'

interface Props {
    customer: MobileOfferCustomer
    offer: MobileOfferCatalogEntry
    request: Omit<AssignMobileOfferRequest, 'provider'>
    onClose: () => void
    onHistoryRefresh: () => void
}

export function CustomerBothStoresReview({ customer, offer, request, onClose, onHistoryRefresh }: Props) {
    const { results, busy, consent, setConsent, confirm, retry } = useBothStoreAssignment(offer, request, onHistoryRefresh)
    const ready = Object.values(results).filter((result) => result.status === 'ready').length
    const checking = Object.values(results).some((result) => result.status === 'checking')
    return (
        <CustomerActionDialog
            title="Review both stores"
            busy={busy}
            onClose={onClose}
            footer={
                <div className="space-y-4">
                    <OfferNotice>
                        Stores are independent. Only ready stores will be assigned. Successful assignments are preserved; no subscription is activated.
                    </OfferNotice>
                    <label className="flex items-start gap-3 text-sm">
                        <input type="checkbox" checked={consent} disabled={busy || checking || !ready} onChange={(event) => setConsent(event.target.checked)} />
                        <span>I confirmed this customer and the terms for each ready store.</span>
                    </label>
                    <div className="flex flex-wrap justify-end gap-3">
                        <OfferButton variant="secondary" disabled={busy} onClick={onClose}>
                            Close review
                        </OfferButton>
                        <OfferButton disabled={busy || checking || !consent || !ready} onClick={() => void confirm()}>
                            {busy ? 'Assigning…' : `Confirm ${ready} store${ready === 1 ? '' : 's'}`}
                        </OfferButton>
                    </div>
                </div>
            }
        >
            <div className="space-y-5">
                <p>
                    {customer.name} · {customer.email}
                    <span className="block break-all text-xs text-slate-500 dark:text-slate-400">Account {customer.userId}</span>
                </p>
                {assignmentStores('both').map((provider) => {
                    const result = results[provider]
                    return (
                        <section
                            key={provider}
                            aria-label={`${customerStoreName(provider)} assignment`}
                            className="space-y-3 rounded-lg border border-slate-200 p-4 dark:border-slate-700"
                        >
                            <h3 className="font-semibold">{customerStoreName(provider)}</h3>
                            <CustomerOfferSummary offer={offer} provider={provider} />
                            {result.status === 'checking' && <OfferNotice>Checking this store…</OfferNotice>}
                            {(result.status === 'ready' || result.status === 'assigned') && (
                                <>
                                    <p className="text-sm">Redeem before {customerOfferDate(result.request.redeemBy)}</p>
                                    <p className="whitespace-pre-wrap break-words text-sm">Reason: {result.request.reason}</p>
                                    {result.request.expectedRevision !== undefined && (
                                        <OfferNotice>Using this store's saved assignment terms. No duplicate assignment will be created.</OfferNotice>
                                    )}
                                    <OfferNotice>{result.status === 'assigned' ? 'Offer assigned. Store delivery confirmed.' : 'Ready to assign.'}</OfferNotice>
                                </>
                            )}
                            {result.status === 'error' && (
                                <>
                                    <OfferNotice error>{result.message}</OfferNotice>
                                    <OfferButton variant="secondary" disabled={busy} onClick={() => retry(provider)}>
                                        Recheck {customerStoreName(provider)}
                                    </OfferButton>
                                </>
                            )}
                        </section>
                    )
                })}
            </div>
        </CustomerActionDialog>
    )
}
